import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotificacionesContext } from "../../context/NotificacionesContext";
import { Menu, Download, User, LogOut, Settings, ChevronDown, Bell } from "lucide-react";
import NotificacionesDropdown from "./NotificacionesDropdown";
import MiPerfil from "./MiPerfil";
import Configuracion from "./Configuracion";

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { stats, cargarStats } = useNotificacionesContext();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificacionesOpen, setNotificacionesOpen] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [configuracionOpen, setConfiguracionOpen] = useState(false);
  const dropdownRef = useRef(null);
  const notificacionesRef = useRef(null);

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    });

    window.addEventListener("appinstalled", () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    // Cargar estadísticas de notificaciones cuando se monta el componente
    if (user) {
      cargarStats();
    }

    // Cerrar dropdowns al hacer click fuera
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificacionesRef.current && !notificacionesRef.current.contains(event.target)) {
        setNotificacionesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  const userInitials = useMemo(() => {
    const name = user?.nombre_completo;
    if (!name) return "U";
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase();
  }, [user?.nombre_completo]);

  const roleFriendlyName = useMemo(() => {
    switch (user?.rol) {
      case "admin":
        return "Administrador";
      case "cliente":
        return "Cliente";
      case "tecnico":
        return "Técnico";
      default:
        return user?.rol || "Usuario";
    }
  }, [user?.rol]);

  const hasUnread = (stats?.no_leidas || 0) > 0;

  // Obtener color de badge según el rol
  const getRoleBadgeColor = (rol) => {
    switch (rol) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "cliente":
        return "bg-emerald-100 text-emerald-800";
      case "tecnico":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
  <header className="relative z-30 mx-2 mt-2 flex h-14 md:mx-4 md:mt-3 md:h-16 items-center justify-between overflow-visible rounded-2xl border border-white/80 bg-white/90 px-3 md:px-5 shadow-[0_18px_40px_-24px_rgba(2,132,199,0.45)] backdrop-blur-xl transition-colors duration-200">
        <div className="pointer-events-none absolute -top-10 left-1/4 h-20 w-40 rounded-full bg-emerald-300/25 blur-2xl" />
        <div className="pointer-events-none absolute -right-8 top-1/2 h-20 w-32 -translate-y-1/2 rounded-full bg-cyan-300/20 blur-2xl" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-300/60 to-transparent" />
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/70" />
        {/* Lado izquierdo */}
        <div className="relative z-10 flex items-center gap-3 md:gap-4">
          {/* Botón menú en móvil - hidden, bottom nav replaces it */}
          <button
            className="hidden rounded-xl p-2 text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900"
            onClick={onMenuClick}
            aria-label="Abrir menú"
          >
            <Menu size={20} />
          </button>

          {/* Logo y título */}
          <div className="flex items-center gap-2 md:gap-3">
            <span className="hidden h-8 w-1 rounded-full bg-gradient-to-b from-emerald-400 to-cyan-400 md:block" />
            <div className="flex flex-col">
              <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.26em] text-emerald-500/90">Control Center</span>
              <h1 className="text-sm md:text-base font-semibold text-slate-900 leading-tight">Elements</h1>
            </div>
            <span className="hidden lg:inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
              Sistema activo
            </span>
          </div>
        </div>

        {/* Lado derecho */}
        <div className="relative z-10 flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/70 p-1 md:gap-3 md:px-1.5 md:py-1 shadow-sm">
          {/* Botón instalar app */}
          {isInstallable && (
            <button
              onClick={handleInstallClick}
              className="group flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition-all hover:-translate-y-0.5 hover:border-emerald-500/60 hover:bg-emerald-100"
            >
              <Download size={16} className="transition-transform group-hover:-translate-y-0.5" />
              <span className="hidden sm:block">Instalar app</span>
            </button>
          )}

          {/* Notificaciones */}
          <div className="relative" ref={notificacionesRef}>
            <button 
              onClick={() => setNotificacionesOpen(!notificacionesOpen)}
              className={`relative rounded-full border p-2 transition-all ${
                notificacionesOpen
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm"
                  : `text-slate-600 hover:border-slate-200 hover:bg-slate-100 hover:text-slate-900 ${hasUnread ? 'border-emerald-200/80 bg-emerald-50/40' : 'border-transparent'}`
              }`}
              aria-label="Notificaciones"
              aria-expanded={notificacionesOpen}
            >
              <Bell size={18} />
              {stats.no_leidas > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex min-h-[1.1rem] min-w-[1.1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[0.65rem] font-semibold text-white shadow-lg">
                  {stats.no_leidas > 99 ? '99+' : stats.no_leidas}
                </span>
              )}
            </button>
            <NotificacionesDropdown 
              isOpen={notificacionesOpen} 
              onClose={() => setNotificacionesOpen(false)} 
            />
          </div>

          {/* Usuario dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2.5 rounded-full border px-2 py-1.5 transition-all ${
                dropdownOpen
                  ? "border-emerald-200 bg-emerald-50/80 shadow-sm"
                  : "border-transparent bg-white/60 hover:border-slate-200 hover:bg-slate-50"
              }`}
              aria-expanded={dropdownOpen}
            >
              {/* Avatar */}
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 via-emerald-500 to-cyan-500 text-sm font-semibold text-white shadow-[0_10px_20px_-12px_rgba(16,185,129,0.8)] ring-2 ring-white">
                {userInitials}
              </div>
              {/* Info usuario - solo visible en pantallas medianas y grandes */}
              <div className="hidden flex-col items-start md:flex">
                <span className="text-sm font-semibold leading-tight text-slate-900">
                  {user?.nombre_completo || user?.nombre || user?.username || roleFriendlyName}
                </span>
                <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getRoleBadgeColor(user?.rol)}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                  {user?.rol || "usuario"}
                </span>
              </div>
              <ChevronDown size={16} className={`hidden text-slate-500 transition-transform md:block ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-3 w-72 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 py-2 shadow-[0_24px_60px_-26px_rgba(15,23,42,0.5)] backdrop-blur-xl z-50">
                {/* Información del usuario */}
                <div className="border-b border-slate-100/80 px-4 py-4 bg-gradient-to-r from-emerald-50/60 via-white to-cyan-50/40">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 via-emerald-500 to-cyan-500 text-sm font-semibold text-white shadow-lg">
                      {userInitials}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{roleFriendlyName}</p>
                      <p className="text-sm text-slate-600">{user?.nombre_completo || user?.username}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getRoleBadgeColor(user?.rol)}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                        {user?.rol || "usuario"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Opciones del menú */}
                <div className="py-2">
                  <button 
                    onClick={() => {
                      setDropdownOpen(false);
                      setPerfilOpen(true);
                    }}
                    className="group flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-emerald-50/60"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-700">
                      <User size={15} />
                    </span>
                    Mi Perfil
                  </button>
                  <button 
                    onClick={() => {
                      setDropdownOpen(false);
                      setConfiguracionOpen(true);
                    }}
                    className="group flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-emerald-50/60"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-emerald-100 group-hover:text-emerald-700">
                      <Settings size={15} />
                    </span>
                    Configuración
                  </button>
                </div>

                {/* Separador */}
                <div className="my-1 border-t border-slate-100/90" />

                {/* Cerrar sesión */}
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    logout();
                  }}
                  className="group flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-red-100/80 text-red-600 group-hover:bg-red-100">
                    <LogOut size={15} />
                  </span>
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modales */}
      <MiPerfil 
        isOpen={perfilOpen} 
        onClose={() => setPerfilOpen(false)} 
      />
      
      <Configuracion 
        isOpen={configuracionOpen} 
        onClose={() => setConfiguracionOpen(false)} 
      />
    </>
  );
}
